import { NORMALIZED_RADAR_SOURCES } from "./radarSourceModes.js";
import { RADAR_SOURCE_MODE_INPUT } from "./radarSourceModes.js";
import {
  isSiataSantaElenaRadarPublicUiEnabled,
  SIATA_SANTA_ELENA_RADAR_ATTRIBUTION,
  SIATA_SANTA_ELENA_RADAR_COVERAGE_LABEL,
  SIATA_SANTA_ELENA_RADAR_SOURCE_CODE,
  SIATA_SANTA_ELENA_RADAR_SOURCE_KEY,
  SIATA_SANTA_ELENA_RADAR_FEATURE_FLAG_ENV,
} from "./siataSantaElenaRadar.js";
import {
  isAerocivilAviationRadarPublicUiEnabled,
  AEROCIVIL_AVIATION_RADAR_ATTRIBUTION,
  AEROCIVIL_AVIATION_RADAR_COVERAGE_LABEL,
  AEROCIVIL_AVIATION_RADAR_SOURCE_CODE,
  AEROCIVIL_AVIATION_RADAR_SOURCE_KEY,
  AEROCIVIL_AVIATION_RADAR_FEATURE_FLAG_ENV,
} from "./aerocivilAviationRadar.js";

/**
 * Resident-facing Weather source taxonomy (Milestone 7).
 * Single source for labels, badges, tones, and plain-language descriptions.
 */

export const WEATHER_SOURCE_TAXONOMY = {
  officialRadar: {
    id: "officialRadar",
    label: "Official radar",
    badge: "Official",
    tone: "official",
    description: "Government radar imagery published by IDEAM.",
    examples: ["IDEAM Imagen Compuesta"],
  },
  ideamBart: {
    id: "ideamBart",
    label: "IDEAM Imagen Compuesta",
    typeLabel: "Official radar",
    badge: "Official",
    tone: "official",
    description: "Official radar composite published by IDEAM.",
    legendType: "Official radar composite",
  },
  siataSantaElenaRadar: {
    key: SIATA_SANTA_ELENA_RADAR_SOURCE_KEY,
    sourceCode: SIATA_SANTA_ELENA_RADAR_SOURCE_CODE,
    id: "siataSantaElenaRadar",
    label: "SIATA Santa Elena Radar",
    typeLabel: "Local official radar",
    trustLabel: "Local official",
    badge: "Local official",
    tone: "localOfficial",
    description:
      "Local radar for Medellín / Aburrá Valley weather monitoring.",
    coverageLabel: SIATA_SANTA_ELENA_RADAR_COVERAGE_LABEL,
    legendType: "Local official radar",
    enabled: isSiataSantaElenaRadarPublicUiEnabled(),
    requiresPartnership: false,
    publicAccessStatus: "approved_development",
    integrationStatus: "approved_development",
    providerStatus: "not_implemented",
    approvalDocPath: "docs/weather/siata-santa-elena-radar-approval.md",
    featureFlagEnv: SIATA_SANTA_ELENA_RADAR_FEATURE_FLAG_ENV,
    helperText: "Local radar for Medellín / Aburrá Valley, when available.",
    fallbackMessage:
      "Local radar unavailable. Showing IDEAM official radar.",
    attribution: SIATA_SANTA_ELENA_RADAR_ATTRIBUTION,
    developmentPreviewLabel:
      "SIATA Santa Elena Radar — Local official — development preview",
  },
  aerocivilAviationRadar: {
    key: AEROCIVIL_AVIATION_RADAR_SOURCE_KEY,
    sourceCode: AEROCIVIL_AVIATION_RADAR_SOURCE_CODE,
    id: "aerocivilAviationRadar",
    label: "Aerocivil aviation radar",
    typeLabel: "Official aeronautical radar",
    trustLabel: "Official (aeronautical)",
    badge: "Official",
    tone: "official",
    description:
      "Official aeronautical radar from Colombia's civil aviation meteorology service. Complementary to IDEAM and SIATA — not a replacement.",
    coverageLabel: AEROCIVIL_AVIATION_RADAR_COVERAGE_LABEL,
    legendType: "Official aeronautical radar",
    enabled: isAerocivilAviationRadarPublicUiEnabled(),
    requiresPartnership: false,
    publicAccessStatus: "public_web_provisional",
    integrationStatus: "discovery_complete",
    providerStatus: "not_implemented",
    approvalDocPath: "docs/weather/aerocivil-radar-approval.md",
    featureFlagEnv: AEROCIVIL_AVIATION_RADAR_FEATURE_FLAG_ENV,
    helperText:
      "Aeronautical radar near major airports and regional sites — compare, not Valley Preferred.",
    fallbackMessage: "Aerocivil aeronautical radar unavailable.",
    attribution: AEROCIVIL_AVIATION_RADAR_ATTRIBUTION,
    developmentPreviewLabel:
      "Aerocivil aviation radar — Official (aeronautical) — development preview",
  },
  backupRadar: {
    id: "backupRadar",
    label: "Backup radar",
    badge: "Backup",
    tone: "backup",
    description:
      "Third-party radar used only when official radar is unavailable or when selected manually.",
    examples: ["RainViewer backup"],
  },
  rainviewer: {
    id: "rainviewer",
    label: "RainViewer backup",
    typeLabel: "Backup radar",
    badge: "Backup",
    tone: "backup",
    description:
      "Third-party radar backup used when official radar is unavailable or when selected manually.",
  },
  estimate: {
    id: "estimate",
    label: "Satellite rain estimate",
    typeLabel: "Satellite estimate",
    badge: "Estimate",
    tone: "estimate",
    description:
      "Satellite-derived rainfall estimate. Useful for context, but not ground-based radar.",
    examples: ["NASA IMERG", "Satellite precipitation"],
  },
  localSensors: {
    id: "localSensors",
    label: "Local sensors",
    badge: "Local sensors",
    tone: "local",
    description: "Ground observations from local monitoring stations.",
    examples: ["SIATA rain gauges", "SIATA stream sensors", "lightning"],
  },
  siataRainGauges: {
    id: "siataRainGauges",
    label: "SIATA rain gauges",
    typeLabel: "Local sensors",
    badge: "Local sensors",
    tone: "local",
    description: "Ground rain readings from SIATA monitoring stations.",
  },
  siataStreams: {
    id: "siataStreams",
    label: "SIATA stream sensors",
    typeLabel: "Local sensors",
    badge: "Local sensors",
    tone: "local",
    description: "Ground stream and quebrada level readings from SIATA.",
  },
  lightning: {
    id: "lightning",
    label: "Recent lightning",
    typeLabel: "Local sensors",
    badge: "Local sensors",
    tone: "local",
    description: "Recent lightning detections from local monitoring.",
  },
  localAlerts: {
    id: "localAlerts",
    label: "SIATA local alerts",
    typeLabel: "Local alerts",
    badge: "Local alert",
    tone: "alerts",
    description: "Environmental alerts published by the local monitoring authority.",
  },
  summary: {
    id: "summary",
    label: "Weather risk context",
    typeLabel: "Summary",
    badge: "Summary",
    tone: "summary",
    description:
      "Plain-language summary for the selected point, generated from available weather layers.",
  },
  communitySignal: {
    id: "communitySignal",
    label: "Community signal",
    typeLabel: "Community signal",
    badge: "Community",
    tone: "reference",
    description: "Community-submitted observations and reports.",
  },
  internalDemo: {
    id: "internalDemo",
    label: "Analyst weather intelligence",
    typeLabel: "Internal analysis",
    badge: "Internal / demo",
    tone: "internal",
    description: "Experimental storm intelligence. Not an official warning.",
    examples: ["storm corridors", "valley storm events", "alert verification"],
  },
  historicalReplay: {
    id: "historicalReplay",
    label: "Historical radar replay",
    typeLabel: "Context / replay",
    badge: "Context / replay",
    tone: "internal",
    description:
      "Short-term review of past radar frames for context. Not an official incident report.",
  },
};

/** Resident-facing trust label glossary (Milestone 16 — public transparency page). */
export const WEATHER_TRUST_LABEL_DEFINITIONS = [
  {
    tone: "official",
    label: "Official",
    description: "Published by a government or official public agency.",
  },
  {
    tone: "official",
    label: "Official (aeronautical)",
    description:
      "Published by an aviation authority for aeronautical weather radar. Not the same as national hydromet (IDEAM) or local valley radar (SIATA).",
  },
  {
    tone: "localOfficial",
    label: "Local official",
    description:
      "Published by a local official or public agency, usually with stronger local coverage.",
  },
  {
    tone: "backup",
    label: "Backup",
    description:
      "Used when official radar is unavailable or as a comparison layer. Backup sources are not official.",
  },
  {
    tone: "estimate",
    label: "Estimate",
    description:
      "Calculated or satellite-based context. Useful for patterns, but not the same as ground radar or official alerts.",
  },
  {
    tone: "local",
    label: "Local sensors",
    description:
      "Ground-based measurements such as rain gauges, stream sensors, or lightning feeds.",
  },
  {
    tone: "alerts",
    label: "Local alert",
    description:
      "Environmental or hydrology alerts published through official local feeds.",
  },
  {
    tone: "reference",
    label: "Community signal",
    description: "Reports from people or community sources. Useful context, but not official.",
  },
  {
    tone: "internal",
    label: "Internal / demo",
    description: "Experimental tools used for analysis. Not official warnings.",
  },
];

/**
 * Public transparency source cards — references taxonomy for labels/badges.
 * Only resident-safe fields; no keys, URLs, or admin diagnostics.
 */
export const WEATHER_TRANSPARENCY_SOURCE_CARD_CONFIG = [
  {
    id: "siata-santa-elena",
    taxonomyKey: "siataSantaElenaRadar",
    publicUseCase: "Local radar context for rain patterns in the Aburrá Valley.",
    publicCoverage: "Aburrá Valley / Medellín local coverage",
    publicLimitations:
      "Local coverage only. Radar imagery does not create official alerts by itself.",
    publicAttribution: SIATA_SANTA_ELENA_RADAR_ATTRIBUTION,
  },
  {
    id: "aerocivil-aviation",
    taxonomyKey: "aerocivilAviationRadar",
    publicUseCase:
      "Aeronautical radar context near airports and regional coverage — compare with national and local radar.",
    publicCoverage: "Colombia aeronautical radar sites (GTA, CRM mosaic, ADZ)",
    publicLimitations:
      "Not local valley radar (SIATA) or national hydromet composite (IDEAM). Does not create official alerts.",
    publicAttribution: AEROCIVIL_AVIATION_RADAR_ATTRIBUTION,
  },
  {
    id: "ideam-bart",
    taxonomyKey: "ideamBart",
    title: "IDEAM BART",
    publicUseCase: "Official national radar context for Colombia.",
    publicCoverage: "Colombia national radar composite, subject to availability",
    publicLimitations: "May be stale, incomplete, or unavailable.",
    publicAttribution:
      "IDEAM — Instituto de Hidrología, Meteorología y Estudios Ambientales.",
  },
  {
    id: "rainviewer",
    taxonomyKey: "rainviewer",
    publicUseCase: "Backup when official radar is unavailable, or for comparison.",
    publicCoverage: "Broad third-party radar and weather visualization",
    publicLimitations: "Not official.",
    publicAttribution: "RainViewer — third-party backup radar visualization.",
  },
  {
    id: "satellite-imerg",
    taxonomyKey: "estimate",
    title: "Satellite / IMERG",
    publicUseCase: "Satellite precipitation estimate and regional context.",
    publicCoverage: "Wider regional satellite coverage where configured",
    publicLimitations: "Not radar, not ground truth, not official alerts.",
    publicAttribution: "Satellite-derived precipitation estimates where configured.",
  },
  {
    id: "siata-sensors",
    taxonomyKey: "localSensors",
    title: "SIATA gauges and stream sensors",
    publicUseCase: "Ground-truth measurements for rain, streams, and quebradas.",
    publicCoverage: "Valle de Aburrá monitoring network",
    publicLimitations: "Sensor coverage and availability vary.",
    publicAttribution: SIATA_SANTA_ELENA_RADAR_ATTRIBUTION,
  },
  {
    id: "official-local-alerts",
    taxonomyKey: "localAlerts",
    title: "Official and local alert feeds",
    badgeLabel: "Official or Local alert",
    publicUseCase: "Actual alerts, warnings, and advisories from official feeds.",
    publicCoverage: "Depends on active alert feeds for the region",
    publicLimitations: "Depends on official feed availability and update cadence.",
    publicAttribution: "SIATA and other official alert publishers where applicable.",
  },
  {
    id: "community-reports",
    taxonomyKey: "communitySignal",
    title: "Community weather reports",
    publicUseCase: "Local observations from people and community reports.",
    publicCoverage: "Where community reports have been submitted",
    publicLimitations: "Not verified official warnings.",
    publicAttribution: null,
  },
  {
    id: "historical-replay",
    taxonomyKey: "historicalReplay",
    title: "Historical radar replay",
    publicUseCase: "Short-term review of past radar frames.",
    publicCoverage: "Recent SIATA local radar archive where available",
    publicLimitations: "Not an official incident report by itself.",
    publicAttribution: null,
  },
];

export function resolveTransparencySourceCard(config) {
  const taxonomy = WEATHER_SOURCE_TAXONOMY[config.taxonomyKey] || {};
  return {
    id: config.id,
    title: config.title || taxonomy.label,
    badgeTone: taxonomy.tone,
    badgeLabel: config.badgeLabel || taxonomy.badge,
    body: taxonomy.description,
    publicUseCase: config.publicUseCase,
    publicCoverage: config.publicCoverage,
    publicLimitations: config.publicLimitations,
    publicAttribution: config.publicAttribution || taxonomy.attribution || null,
  };
}

export function resolveTransparencySourceCards() {
  return WEATHER_TRANSPARENCY_SOURCE_CARD_CONFIG.map(resolveTransparencySourceCard);
}

export const WEATHER_STATUS_MESSAGES = {
  fallbackActive: "Official radar unavailable. Showing RainViewer backup.",
  fallbackReady: "Backup radar is available if official radar becomes unavailable.",
  backupSelected: "Backup source selected manually.",
  officialUnavailable: "Official radar selected, but no renderable frames are available.",
  officialStale: "Official radar may be stale.",
  noRadarFrames: "No renderable radar frames are available.",
  radarUnavailable: "No renderable radar source is available.",
  satelliteEstimateNote:
    "Satellite-derived estimate, not ground-based radar.",
  internalAnalysisNote: "Internal analysis. Not an official warning.",
  mapClickIntro:
    "This is a summary of available weather context for the selected point.",
  mapClickInternalNote: "Internal analysis is not an official warning.",
  preferredModeHelper:
    "Preferred mode uses official IDEAM radar when available and clearly switches to backup when needed.",
  rainviewerOnlyHelper:
    "RainViewer is a backup source, not an official Colombian radar product.",
  analystToolsBanner:
    "Analyst Tools include experimental storm motion, replay, corridor, and verification features. These are internal analysis tools and are not official warnings.",
  radarAnimationUnavailable: "Radar animation unavailable.",
  layersOff: "Layers off",
};

export function getWeatherSourceTaxonomy(key) {
  return WEATHER_SOURCE_TAXONOMY[key] || null;
}

export function resolveRenderedRadarPresentation({
  renderedSource,
  fallbackActive = false,
  sourceModeInput = RADAR_SOURCE_MODE_INPUT.PREFERRED,
  message = "",
} = {}) {
  if (renderedSource === NORMALIZED_RADAR_SOURCES.IDEAM_BART) {
    return {
      ...WEATHER_SOURCE_TAXONOMY.ideamBart,
      headline: `Radar: ${WEATHER_SOURCE_TAXONOMY.ideamBart.label}`,
      detail: WEATHER_SOURCE_TAXONOMY.ideamBart.description,
      legendMessage: null,
    };
  }

  if (renderedSource === NORMALIZED_RADAR_SOURCES.RAINVIEWER) {
    const detail = fallbackActive
      ? WEATHER_STATUS_MESSAGES.fallbackActive
      : sourceModeInput === RADAR_SOURCE_MODE_INPUT.RAINVIEWER
        ? WEATHER_STATUS_MESSAGES.backupSelected
        : WEATHER_SOURCE_TAXONOMY.rainviewer.description;

    return {
      ...WEATHER_SOURCE_TAXONOMY.rainviewer,
      headline: `Radar: ${WEATHER_SOURCE_TAXONOMY.rainviewer.label}`,
      detail,
      legendMessage: detail,
      fallbackActive,
    };
  }

  if (renderedSource === NORMALIZED_RADAR_SOURCES.IDEAM_OSPA) {
    return {
      label: "Legacy IDEAM OSPA",
      typeLabel: "Legacy diagnostic",
      badge: "Legacy",
      tone: "official",
      headline: "Radar: Legacy IDEAM OSPA",
      detail: "Legacy diagnostic radar layer. Not the default official source.",
      legendMessage: "Legacy diagnostic radar layer.",
    };
  }

  if (renderedSource === NORMALIZED_RADAR_SOURCES.SIATA_SANTA_ELENA) {
    return {
      ...WEATHER_SOURCE_TAXONOMY.siataSantaElenaRadar,
      headline: `Radar: ${WEATHER_SOURCE_TAXONOMY.siataSantaElenaRadar.label}`,
      detail: message || WEATHER_SOURCE_TAXONOMY.siataSantaElenaRadar.description,
      legendMessage: message || null,
    };
  }

  return {
    label: "Radar unavailable",
    badge: null,
    tone: null,
    headline: "Radar unavailable",
    detail: message || WEATHER_STATUS_MESSAGES.radarUnavailable,
    legendMessage: message || WEATHER_STATUS_MESSAGES.radarUnavailable,
  };
}

export function resolveRadarSourceModeHelperText(sourceMode, radarSourceStatus = null) {
  const mode = sourceMode || RADAR_SOURCE_MODE_INPUT.PREFERRED;

  if (mode === RADAR_SOURCE_MODE_INPUT.PREFERRED) {
    return WEATHER_STATUS_MESSAGES.preferredModeHelper;
  }

  if (mode === RADAR_SOURCE_MODE_INPUT.RAINVIEWER) {
    return WEATHER_STATUS_MESSAGES.rainviewerOnlyHelper;
  }

  if (mode === RADAR_SOURCE_MODE_INPUT.IDEAM_BART) {
    if (radarSourceStatus?.renderedSource === NORMALIZED_RADAR_SOURCES.NONE) {
      return WEATHER_STATUS_MESSAGES.officialUnavailable;
    }
    return WEATHER_SOURCE_TAXONOMY.ideamBart.description;
  }

  if (mode === RADAR_SOURCE_MODE_INPUT.LEGACY_OSPA) {
    return "Legacy IDEAM OSPA diagnostic layer for advanced comparison only.";
  }

  if (mode === RADAR_SOURCE_MODE_INPUT.VALLEY_PREFERRED) {
    if (radarSourceStatus?.message) {
      return radarSourceStatus.message;
    }
    return "Best for Medellín / Aburrá Valley. Uses SIATA local radar when healthy, then IDEAM, then RainViewer backup.";
  }

  if (mode === RADAR_SOURCE_MODE_INPUT.SIATA_LOCAL_ONLY) {
    return "Use SIATA Santa Elena Radar only. No automatic fallback.";
  }

  return null;
}

export function resolveHeroSourceTags(intel = {}) {
  const tags = [];
  const radarStatus = intel.radarSourceStatus;
  const rendered = radarStatus?.renderedSource;
  const fallback = radarStatus?.fallbackActive || intel.radarUsingFallback;

  if (intel.ideamRadarLayerEnabled) {
    if (rendered === NORMALIZED_RADAR_SOURCES.IDEAM_BART) {
      tags.push({
        tone: WEATHER_SOURCE_TAXONOMY.officialRadar.tone,
        label: WEATHER_SOURCE_TAXONOMY.officialRadar.label,
      });
    } else if (rendered === NORMALIZED_RADAR_SOURCES.RAINVIEWER && fallback) {
      tags.push({
        tone: WEATHER_SOURCE_TAXONOMY.backupRadar.tone,
        label: "Backup active",
      });
    } else if (rendered === NORMALIZED_RADAR_SOURCES.RAINVIEWER) {
      tags.push({
        tone: WEATHER_SOURCE_TAXONOMY.rainviewer.tone,
        label: WEATHER_SOURCE_TAXONOMY.rainviewer.label,
      });
    } else if (rendered === NORMALIZED_RADAR_SOURCES.IDEAM_OSPA) {
      tags.push({ tone: "official", label: "Legacy diagnostic" });
    } else if (!fallback) {
      tags.push({
        tone: WEATHER_SOURCE_TAXONOMY.officialRadar.tone,
        label: WEATHER_SOURCE_TAXONOMY.officialRadar.label,
      });
    }
  } else if (fallback || intel.rainviewerBackupLayerEnabled) {
    tags.push({
      tone: WEATHER_SOURCE_TAXONOMY.backupRadar.tone,
      label: "Backup ready",
    });
  }

  if (intel.satellitePrecipLayerEnabled) {
    tags.push({
      tone: WEATHER_SOURCE_TAXONOMY.estimate.tone,
      label: WEATHER_SOURCE_TAXONOMY.estimate.typeLabel,
    });
  }

  if (
    intel.precipitationStationsEnabled ||
    intel.riverLevelsEnabled ||
    intel.lightningStrikesEnabled
  ) {
    tags.push({
      tone: WEATHER_SOURCE_TAXONOMY.localSensors.tone,
      label: WEATHER_SOURCE_TAXONOMY.localSensors.label,
    });
  }

  if (intel.environmentalAlertsEnabled) {
    tags.push({
      tone: WEATHER_SOURCE_TAXONOMY.localAlerts.tone,
      label: WEATHER_SOURCE_TAXONOMY.localAlerts.typeLabel,
    });
  }

  if (!tags.length) {
    tags.push({
      tone: WEATHER_SOURCE_TAXONOMY.summary.tone,
      label: WEATHER_STATUS_MESSAGES.layersOff,
    });
  }

  return tags;
}

export function resolveMapClickRadarLabel(intel = {}) {
  const rendered = intel.radarSourceStatus?.renderedSource;
  const fallback = intel.radarSourceStatus?.fallbackActive || intel.radarUsingFallback;

  if (rendered === NORMALIZED_RADAR_SOURCES.RAINVIEWER || fallback) {
    return {
      label: "Radar rain",
      sourceType: WEATHER_SOURCE_TAXONOMY.rainviewer.typeLabel,
      badge: WEATHER_SOURCE_TAXONOMY.rainviewer.badge,
      tone: WEATHER_SOURCE_TAXONOMY.rainviewer.tone,
    };
  }

  return {
    label: "Radar rain",
    sourceType: WEATHER_SOURCE_TAXONOMY.officialRadar.label,
    badge: WEATHER_SOURCE_TAXONOMY.officialRadar.badge,
    tone: WEATHER_SOURCE_TAXONOMY.officialRadar.tone,
  };
}
